import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewChild, inject } from '@angular/core';
import { ImageModule } from '@df/module-image/image.module';
import { CarouselComponent } from '@df/ui-carousel/carousel.component';
import { CarouselModule } from '@df/ui-carousel/carousel.module';
import { RatioOptions } from '@df/ui/atomic/atomic-options';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { ICmsDataChangeService, NICmsDataChange } from '@icms/core/data/change/icms-data-change.service';
export namespace NCmsCarousel {
    export interface IComponentData extends NICmsContentComponent.IData {
        slides: ISlide[];
        autoplay: boolean;
        interval: number;
        loop: boolean;
        pagination: boolean;
        arrows: boolean;
        progress: boolean;
        ratio: string;
    }
    export interface ISlide {
        custom: boolean;
        image: string;
        components: NICmsContentComponent.IData[];
    }
}

let uid = 0;
@ICmsComponent<CmsCarouselComponent>({
    id: 'carousel',
    name: 'Carousel',
    description: 'Carousel with slides',
    tags: ['banner', 'image', 'carousel'],
    fields: [
        // general
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-general',
            title: 'General'
        },
        {
            key: 'autoplay',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Autoplay',
            default: false,
            canShow: (data: NCmsCarousel.IComponentData) => data.slides.length > 1
        },
        {
            key: 'interval',
            type: NICmsContentField.EFieldType.number,
            title: 'Interval',
            canShow: (data: NCmsCarousel.IComponentData) => !!data.autoplay && data.slides.length > 1,
            default: 5000
        },
        {
            key: 'loop',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Loop',
            default: false,
            canShow: (data: NCmsCarousel.IComponentData) => data.slides.length > 1
        },
        {
            key: 'pagination',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Show Bullet Controllers',
            default: true,
            canShow: (data: NCmsCarousel.IComponentData) => data.slides.length > 1
        },
        {
            key: 'arrows',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Show Navigate Arrows',
            default: true,
            canShow: (data: NCmsCarousel.IComponentData) => data.slides.length > 1
        },
        {
            key: 'progress',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Show Progress Bar',
            default: true,
            canShow: (data: NCmsCarousel.IComponentData) => data.slides.length > 1
        },
        <NICmsContentField.IChoiceField>{
            key: 'ratio',
            type: NICmsContentField.EFieldType.choice,
            title: 'Ratio',
            options: RatioOptions,
            description: 'Proportions width x height',
            default: 'ratio-2-1'
        },
        // slides
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'slides',
            title: 'Slides',
            itemName: 'Slide',
            fields: [
                {
                    key: 'custom',
                    type: NICmsContentField.EFieldType.toggle,
                    title: 'Custom Content',
                    default: false
                },
                {
                    key: 'image',
                    type: NICmsContentField.EFieldType.image,
                    title: 'Image',
                    default: '',
                    canShow: (data: NCmsCarousel.ISlide) => !data.custom,
                    required: true
                },
                <NICmsContentField.IComponentsField>{
                    type: NICmsContentField.EFieldType.components,
                    title: 'Components',
                    itemName: 'component',
                    key: 'components',
                    default: [],
                    canShow: (data: NCmsCarousel.ISlide) => data.custom
                }
            ],
            defaultSize: 2
        }
    ]
})
@Component({
    selector: 'cms-carousel',
    templateUrl: './cms-carousel.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CarouselModule, CommonModule, ICmsClientModule, ImageModule]
})
export class CmsCarouselComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component?: NCmsCarousel.IComponentData;

    uid = `cmsCarousel-${uid++}`;
    /**
     * Variable required by carousel component.
     *
     * @type {number}
     * @memberof PageComponentCarouselComponent
     */
    currentIndex = 0;

    iCmsDataChangeService = inject(ICmsDataChangeService);

    @ViewChild('carouselRef')
    carouselRef?: CarouselComponent;

    override ngOnInit() {
        super.ngOnInit();

        this.subscriptions.add(
            this.iCmsDataChangeService.afterChange$.subscribe(event => {
                if (event.changeType === NICmsDataChange.EChangeType.change) {
                    if (this.cmsObserveAutoplayData.some(path => event.valuePath.includes(path))) {
                        if (this.carouselRef) {
                            if (this.component?.autoplay) {
                                this.carouselRef.swiperRef.params.autoplay = { delay: this.component.interval };
                                this.carouselRef.swiperRef.autoplay.start();
                            } else {
                                this.carouselRef.swiperRef.params.autoplay = false;
                                this.carouselRef.swiperRef.autoplay.stop();
                            }
                        }
                        this.detectChanges();
                    }

                    if (this.cmsObservedData.some(path => event.valuePath.includes(path))) {
                        this.carouselRef?.initSwiper();
                    }
                }
            })
        );
    }

    get cmsObserveAutoplayData(): string[] {
        return ['autoplay'];
    }

    get cmsObservedData(): string[] {
        return ['pagination', 'progress'];
    }
}
